import {
  Button,
  CircularProgress,
  FormControl,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../../firebase-setup/firebase';
import {
  collection,
  doc,
  DocumentData,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import { formatPhoneNumber } from '../../utils/utilFuntions';
import { useCollection } from 'react-firebase-hooks/firestore';
import PersonaSelector from './PersonaSelector';
import { docInfo } from '../../utils/types';
import { updateDoctor } from '../../utils/sfFunctions';

const url = new URL(window.location.href);
const searchParams = new URLSearchParams(url.search);
interface reCreateOrdersPayload {
  patient: {
    email: string;
    ssn: string;
  };
  hp: {
    facility: string;
    lname: string;
  };
}
async function reCreateOrders(payload: reCreateOrdersPayload) {
  fetch(
    'https://us-central1-transparent-rx.cloudfunctions.net/reCreateOrdersSF',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ payload }),
    }
  )
    .then((response) => response.json())
    .then((data) => {
      console.log('Success:', data);
    })
    .catch((error) => {
      console.error('Error:', error);
    });
}
function HpFaxUpdating() {
  const [clientList, setClientList] = useState<string[]>([]);
  const [clientsData, loadingClientsData] = useCollection(
    collection(db, 'clients'),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );
  const [selectedClient, setSelectedClient] = useState<DocumentData | null>(
    null
  );
  const [hpList, setHpList] = useState<
    {
      id: string;
      name: string;
      fax: string;
    }[]
  >([]);
  const [selectedHP, setSelectedHP] = useState<DocumentData | null>(null);

  const [fieldError, setFieldError] = useState<string | null>(null);
  const [hpFax, setHpFax] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [updated, setUpdated] = useState(false);
  const lastFetchedSSN = useRef<string | null>(null);
  const [hpListReady, setHpListReady] = useState(false);

  const getHpList = useCallback(async (selectedClientSSN: string) => {
    setHpListReady(false);
    const hpCollectionRef = collection(
      db,
      `clients/${selectedClientSSN}/healthcare-providers`
    );
    const hpSnapshot = await getDocs(hpCollectionRef);
    const hpList = hpSnapshot.docs
      .map((doc) => {
        const data = doc.data();
        // Construct full name depending on whether mname exists and is non-empty
        const fullName = `${data.fname} ${data.mname || ''} ${data.lname}`
          .replace(/\s+/g, ' ')
          .trim();
        return {
          data: data,
          id: doc.id,
          name: fullName.trim(),
          fax: data.fax,
        };
      })
      .filter((hp) => hp.name !== '');
    setHpList(hpList);
    console.log(hpList);
    setHpListReady(true);
  }, []);

  const updateHpFax = useCallback(
    async (clientId: string, hpDocId: string, docInfo: docInfo) => {
      if (!clientId || !hpDocId) {
        setError('Missing client or provider ID.');
        console.error('Missing clientId or hpDocId:', { clientId, hpDocId });
        return;
      }

      try {
        const clientDoc = doc(collection(db, 'clients'), clientId);
        console.log('clientDoc', clientDoc);
        const hpDoc = doc(
          collection(clientDoc, 'healthcare-providers'),
          hpDocId
        );
        await updateDoc(hpDoc, {
          fax: hpFax,
        });
        await updateDoctor(docInfo)
          .then(() => {
            console.log('HP fax updated successfully');
          })
          .catch((error) => {
            console.error('Error updating HP fax:', error);
          });
        const payload: reCreateOrdersPayload = {
          patient: {
            email: selectedClient!.email,
            ssn: selectedClient!.ssn,
          },
          hp: {
            facility: selectedHP!.data.facility,
            lname: selectedHP!.data.lname,
          },
        };
        await reCreateOrders(payload);
        setUpdated(true);
      } catch (error) {
        setError('An error occurred while updating the fax number.');
        console.error('Error updating HP Fax:', error);
      }
    },
    [hpFax, selectedClient, selectedHP]
  );
  const handleClientChange = useCallback(
    async (value: string) => {
      const clientDoc = clientsData!.docs.find((doc) => {
        const data = doc.data();
        return `${data.fname} ${data.lname} (${data.email})` === value;
      });
      if (clientDoc) {
        setSelectedClient(clientDoc.data());
        setHpList([]);
        setHpFax(null);
        setError(null);
        setUpdated(false);
        setSelectedHP(null);
      }
    },
    [clientsData]
  );

  const handleHpChange = useCallback(
    async (value: string) => {
      const hpDoc = hpList.find((hp) => hp.name === value);
      console.log('hpDoc', hpDoc);
      if (hpDoc) {
        setSelectedHP(hpDoc);
        setHpFax(hpDoc.fax);
        setFieldError(null); // Reset error when a new HP is selected
        setError(null); // Reset error when a new client is selected
        setUpdated(false); // Reset updated state when a new client is selected
      }
    },
    [hpList]
  );
  const validateFields = useCallback((value: string) => {
    let error = '';

    // Ensure value is properly formatted before checking
    const formattedValue = value.trim();

    // Validate correct format
    if (!/^\(\d{3}\) \d{3} - \d{4}$/.test(formattedValue)) {
      error = 'Invalid fax number';
    }

    console.log('error', error);
    return error;
  }, []);

  const handleChangeWithValidation = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let fieldValue = event.target.value;

      fieldValue = formatPhoneNumber(event);
      // Perform validation
      const error = validateFields(fieldValue);
      setFieldError(error);
      setHpFax(fieldValue);
      const newSelectedHP = selectedHP;
      newSelectedHP!.data.fax = fieldValue;
      setSelectedHP(newSelectedHP);
    },
    [selectedHP, validateFields]
  );

  useEffect(() => {
    async function fetchParams() {
      const clientName = searchParams.get('clientName');
      const clientEmail = searchParams.get('clientEmail');

      const clientSearchName = `${clientName} (${clientEmail})`;
      const hpDocId = searchParams.get('hpId');
      const hpName = searchParams.get('hpName');

      if (!clientName || !hpDocId || !hpName) {
        return;
      }
      await handleClientChange(clientSearchName);
    }
    clientList.length !== 0 && fetchParams();
  }, [clientList.length, handleClientChange, handleHpChange, selectedClient]);

  useEffect(() => {
    if (!loadingClientsData && clientsData) {
      const orderedList = clientsData!.docs.map((doc) => {
        if (
          doc.data().insurance.company === 'medicare' ||
          doc.data().employed === 'No Income in Household'
        )
          return (
            doc.data().fname +
            ' ' +
            doc.data().lname +
            ' (' +
            doc.data().email +
            ')'
          );
      });

      orderedList.sort((a, b) => {
        if (a! > b!) return 1;
        if (a! < b!) return -1;
        return 0;
      });
      setClientList(
        orderedList.filter((client) => client !== undefined) as string[]
      );
    }
  }, [loadingClientsData, clientsData]);

  //update hp when client selected
  useEffect(() => {
    if (
      selectedClient &&
      selectedClient.ssn &&
      selectedClient.ssn !== lastFetchedSSN.current
    ) {
      lastFetchedSSN.current = selectedClient.ssn;
      getHpList(selectedClient.ssn);
    }
  }, [selectedClient, getHpList]);

  useEffect(() => {
    const hpName = searchParams.get('hpName');
    if (!selectedHP && hpList.length > 0 && hpName) {
      handleHpChange(hpName);
      searchParams.delete('clientName');
      searchParams.delete('clientEmail');
      searchParams.delete('hpId');
      searchParams.delete('hpName');
    }
  }, [hpList, handleHpChange, selectedHP]);

  return (
    <Stack className="container" sx={{ mt: 8 }}>
      {!loadingClientsData && clientsData ? (
        <>
          <Stack justifyContent={'center'} alignItems={'center'} gap={4}>
            <Typography variant="h2">
              Update Healthcare Provider Fax Number
            </Typography>
            <Stack
              gap={4}
              alignItems={'stretch'}
              width={'100%'}
              maxWidth={'80rem'}>
              <PersonaSelector
                clientList={clientList}
                selectedClient={selectedClient}
                onClientChange={handleClientChange}
                mode="client"
              />
              {selectedClient && hpListReady && (
                <PersonaSelector
                  clientList={hpList.map((hp) => hp.name)}
                  selectedClient={selectedHP}
                  onClientChange={handleHpChange}
                  mode="hp"
                />
              )}
              {selectedHP &&
                (error ? (
                  <Stack
                    className="tprx-card"
                    style={{
                      margin: 'auto',
                      width: '50rem',
                      maxWidth: '90%',
                    }}>
                    <Typography variant="h2" sx={{ textAlign: 'center' }}>
                      Error
                    </Typography>
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                      An error occurred: please contact support.
                    </Typography>
                  </Stack>
                ) : updated ? (
                  <Stack
                    className="tprx-card"
                    style={{
                      margin: 'auto',
                      width: '50rem',
                      maxWidth: '90%',
                    }}>
                    <Stack spacing={1}>
                      <Typography variant="h2" sx={{ textAlign: 'center' }}>
                        Fax number Updated
                      </Typography>
                      <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        The automation will try to continue the flow with the
                        new fax number.
                      </Typography>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack
                    gap={4}
                    className="tprx-card"
                    style={{
                      margin: 'auto',
                      width: '50rem',
                      maxWidth: '90%',
                    }}>
                    <Stack spacing={1}>
                      <Typography variant="h2" sx={{ textAlign: 'center' }}>
                        Update {selectedHP.name} Fax Number
                      </Typography>
                    </Stack>
                    <FormControl
                      sx={{
                        width: '100%',
                      }}>
                      <Stack spacing={2}>
                        <TextField
                          label="Enter Healthcare Provider Fax Number"
                          variant="outlined"
                          value={hpFax || ''}
                          onChange={handleChangeWithValidation}
                          error={Boolean(fieldError)}
                          helperText={fieldError}
                        />
                        <Button
                          disabled={
                            Boolean(fieldError) ||
                            !hpFax ||
                            loading ||
                            hpFax?.trim() === selectedHP.fax?.trim()
                          }
                          variant="contained"
                          color="primary"
                          onClick={async () => {
                            setLoading(true);
                            await updateHpFax(
                              selectedClient!.ssn,
                              selectedHP.id,
                              selectedHP.data as docInfo
                            );
                            setLoading(false);
                          }}>
                          {loading
                            ? 'Updating...'
                            : hpFax?.trim() === selectedHP.fax?.trim()
                            ? 'Change the current fax number'
                            : 'Update HP Fax'}
                        </Button>
                      </Stack>
                    </FormControl>
                  </Stack>
                ))}
            </Stack>
          </Stack>
        </>
      ) : (
        <div className="loader-s">
          <CircularProgress />
        </div>
      )}
    </Stack>
  );
}
export default HpFaxUpdating;
